import React, { useEffect, useState, useRef } from "react";
import { Menu, X, ArrowRight, Share2 } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { isLowPowerClient } from "../../lib/platform";
import Logo from "../Logo";
import { brand } from "../../brand";
import { liveStats } from "../../data/mock";
import { shareLink } from "../../lib/share";
import api from "../../lib/api";
import { HeroWindow, HeroLiveBadge, HeroJoinTicker } from "./HeroStage";
import StoreBadges from "./StoreBadges";

function useCountUp(target, start = true, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const startT = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startT) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
}

export function LandingNav({ onJoin, onLogin }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong border-b border-white/[0.06]" : ""
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Logo size={30} />
        <nav className="hidden items-center gap-9 lg:flex">
          {brand.nav.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="text-sm font-medium text-mist-muted transition-colors hover:text-mist"
            >
              {n.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <button onClick={() => shareLink({ title: "Get WYVN — win daily.", text: `${brand.tagline} Get WYVN at ${brand.site}`, url: `https://${brand.site}` })} className="flex items-center gap-1.5 text-sm font-medium text-mist-muted transition-colors hover:text-mist" aria-label="Share">
            <Share2 size={15} /> Share
          </button>
          <button onClick={onLogin} className="text-sm font-medium text-mist-muted transition-colors hover:text-mist">
            Sign in
          </button>
          <button onClick={onJoin} className="btn-primary text-sm inline-flex items-center gap-1.5">
            Get WYVN <ArrowRight size={15} />
          </button>
        </div>
        <button className="lg:hidden text-mist" onClick={() => setOpen((v) => !v)} aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="glass-strong border-t border-white/[0.06] px-5 py-5 lg:hidden">
          <div className="flex flex-col gap-4">
            {brand.nav.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="text-mist-muted">
                {n.label}
              </a>
            ))}
            <div className="mt-2 flex gap-3">
              <button onClick={onLogin} className="btn-ghost flex-1 text-sm">Sign in</button>
              <button onClick={onJoin} className="btn-primary flex-1 text-sm">Get WYVN</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function HeroGlow({ targetRef }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  return (
    <motion.div style={{ y }} className="pointer-events-none absolute inset-0">
      <div className="absolute -top-40 left-1/2 h-[640px] w-[1000px] -translate-x-1/2 rounded-full bg-brand/12 blur-[170px]" />
      <div className="absolute top-24 left-1/2 h-[340px] w-[520px] -translate-x-1/2 rounded-full bg-brand-soft/10 blur-[130px]" />
    </motion.div>
  );
}
function HeroText({ targetRef, children }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return <motion.div style={{ y, opacity }} className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">{children}</motion.div>;
}
function HeroWindowTilt({ targetRef, children }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start 0.9", "start 0.15"] });
  const rotateX = useTransform(scrollYProgress, [0, 1], [16, 0.5]);
  const y = useTransform(scrollYProgress, [0, 1], [80, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.93, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [0.35, 1]);
  return <motion.div style={{ rotateX, y, scale, opacity, transformPerspective: 1600 }} className="relative origin-top">{children}</motion.div>;
}

export function Hero({ onJoin }) {
  const ref = useRef(null);
  const winRef = useRef(null);
  const lite = isLowPowerClient();

  const heroCopy = (
    <>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs font-medium text-brand-soft backdrop-blur-md"
      >
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-70" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-sage" />
        </span>
        A global wellness movement, woven together
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
        className="text-4xl font-semibold leading-[1.04] tracking-tight sm:text-6xl lg:text-[4.75rem]"
      >
        <span className="text-mist">Win daily.</span>
        <br />
        <span className="text-gradient">Woven forever.</span>
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-mist-muted sm:text-lg"
      >
        {brand.subline}
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.32, ease: [0.22, 1, 0.36, 1] }}
        className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row"
      >
        <button onClick={onJoin} data-testid="hero-get-wyvn-button" className="btn-primary inline-flex items-center gap-2 text-base">
          Get WYVN <ArrowRight size={17} />
        </button>
        <a href="#app" className="btn-ghost text-base">See the app</a>
      </motion.div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.5 }}
        className="mt-5 text-xs text-mist-muted"
      >
        30 days free · No card needed · Growing worldwide
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6 }}
      >
        <StoreBadges className="mt-7" />
      </motion.div>
    </>
  );

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 pb-16 lg:pt-40">
      {/* Premium aurora bloom — soft ambient light behind the headline (replaces the old beam) */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[620px] overflow-hidden">
        <div className="aurora-breathe absolute left-1/2 top-[-260px] h-[560px] w-[1300px] -translate-x-1/2 rounded-[100%] bg-[radial-gradient(closest-side,rgba(106,166,255,0.22),rgba(106,166,255,0.07)_46%,transparent_74%)] blur-[46px]" />
        <div className="aurora-breathe absolute left-1/2 top-[-140px] h-[320px] w-[820px] -translate-x-1/2 rounded-[100%] bg-[radial-gradient(closest-side,rgba(168,150,255,0.14),transparent_72%)] blur-[64px]" style={{ animationDelay: "1.5s" }} />
        <div className="absolute inset-x-0 top-0 mx-auto h-px w-[62%] bg-gradient-to-r from-transparent via-white/12 to-transparent" />
      </div>
      {/* Cinematic film grain */}
      <div className="grain-overlay pointer-events-none absolute inset-0" />
      {lite ? (
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 left-1/2 h-[640px] w-[1000px] -translate-x-1/2 rounded-full bg-brand/12 blur-[170px]" />
          <div className="absolute top-24 left-1/2 h-[340px] w-[520px] -translate-x-1/2 rounded-full bg-brand-soft/10 blur-[130px]" />
        </div>
      ) : (
        <HeroGlow targetRef={ref} />
      )}
      <div className="pointer-events-none absolute inset-0 grid-fade" />

      {lite ? (
        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">{heroCopy}</div>
      ) : (
        <HeroText targetRef={ref}>{heroCopy}</HeroText>
      )}

      {/* hero product window */}
      <div ref={winRef} className="relative mx-auto mt-16 max-w-5xl px-5 [perspective:1600px] lg:mt-20 lg:px-8">
        <div className="pointer-events-none absolute -inset-x-10 top-10 h-[70%] rounded-[3rem] bg-brand/10 blur-[120px]" />
        {lite ? (
          <div className="relative origin-top" style={{ transformPerspective: 1600 }}>
            <HeroWindow />
          </div>
        ) : (
          <HeroWindowTilt targetRef={winRef}>
            <HeroWindow />
          </HeroWindowTilt>
        )}
        {/* floating live-stats card — straddles the window's top-right edge, above the content */}
        <div className="pointer-events-none absolute -top-5 right-4 z-20 hidden sm:block lg:right-8">
          <HeroLiveBadge />
        </div>
        {/* social-proof ticker — straddles the window's bottom-left edge, below the content */}
        <div className="pointer-events-none absolute -bottom-5 left-4 z-20 hidden lg:block">
          <HeroJoinTicker />
        </div>
      </div>
    </section>
  );
}

export function LiveStatsBar() {
  const [stats, setStats] = useState(liveStats);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const load = () => api.publicStats().then(setStats).catch(() => {});
    load();
    const t = setInterval(load, 20000); // live ticker: refresh every 20s
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const members = useCountUp(stats.members, inView);
  const countries = useCountUp(stats.countries, inView);
  const checkins = useCountUp(stats.checkins, inView);
  const items = [
    { label: "Members", value: members.toLocaleString() },
    { label: "Countries", value: countries },
    { label: "Check-ins", value: checkins.toLocaleString() },
    { label: "AI Coaches", value: stats.coaches },
  ];
  return (
    <section ref={ref} className="relative mx-auto max-w-6xl px-5 lg:px-8">
      <div className="glass flex flex-col items-center gap-6 rounded-3xl px-6 py-7 sm:flex-row sm:justify-between sm:px-10">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-mist-muted">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-70" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-sage" />
          </span>
          Live now
        </div>
        <div className="grid w-full grid-cols-2 gap-6 sm:flex sm:w-auto sm:gap-12">
          {items.map((it) => (
            <div key={it.label} className="text-center sm:text-left">
              <div className="font-display text-2xl font-bold text-mist sm:text-3xl">{it.value}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-mist-muted">{it.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
